/**
 * useWorkforceOverviewReport — V70.28
 *
 * `조직관리 > 인력 현황` Dashboard 집계 데이터 조회.
 *
 * 설계
 *  - 서버 callable `getWorkforceOverviewReport` **1개**만 호출한다 (N+1 없음).
 *  - **조직 범위를 FE 에서 보내지 않는다.** 서버가 설정 SSoT(`workforceOverviewSettings`)에서
 *    읽고 subtree 전개까지 수행하므로, FE 가 orgUnitIds 를 조작해 범위를 넓힐 수 없다.
 *  - 권한도 서버(`assertWorkforceOverviewAccess`)가 재검증한다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

export type EmploymentTypeCounts = Record<string, number>;

export interface WorkforceTypeRow {
  code: string;
  label: string;
  count: number;
  ratio: number;
}
export interface WorkforceMonthlyTrendRow {
  ym: string;
  /** 'N월' */
  label: string;
  month: number;
  /** monthEnd = 과거 월말 실적 / today = 당월 / projected = 미래 예정치 */
  basis: 'monthEnd' | 'today' | 'projected';
  asOfDate: string;
  total: number;
  byType: EmploymentTypeCounts;
}
export interface WorkforceJoinLeaveRow {
  ym: string;
  label: string;
  month: number;
  basis: 'past' | 'current' | 'future';
  joins: number;
  joinsScheduled: number;
  leaves: number;
  leavesScheduled: number;
  net: number;
}
/**
 * V70.37: 계약 만료 결과 — 확정 3상태 + 고용형태별 세분화
 *   total   = 만료 도래 전체 (확정 여부와 무관하게 절대 빠지지 않는다)
 *   decided = 전환 + 연장 + 종료 (= 모든 비율의 분모) → 항상 total >= decided
 *   unplanned = 실행도 예약도 없는 만료 건. 화면에서 '미결' 같은 상태로 노출하지 않고
 *               비율 분모에서만 제외한다 (결과를 추측하지 않기 위함).
 */
export interface WorkforceContractEndDetail {
  total: number;
  decided: number;
  converted: number;
  extended: number;
  ended: number;
  unplanned: number;
  /** 후속 고용형태별 전환 건수 */
  convertedByType: EmploymentTypeCounts;
  /** 연장 당시 고용형태별 연장 건수 */
  extendedByType: EmploymentTypeCounts;
  convertedRate: number;
  extendedRate: number;
  endedRate: number;
  convertedRateByType: EmploymentTypeCounts;
  extendedRateByType: EmploymentTypeCounts;
}
export interface WorkforceMonthlyContractEndRow extends WorkforceContractEndDetail {
  ym: string;
  /** 'YYYY.MM' */
  label: string;
  year: number;
  month: number;
  from: string;
  to: string;
}
export interface WorkforceOrgContractEndRow extends WorkforceContractEndDetail {
  orgUnitId: string;
  orgUnitName: string;
}

export interface WorkforceOrgRow {
  orgUnitId: string;
  orgUnitName: string;
  /** V70.30: 해당 조직에 **직접 소속**된 인원 (하위 조직 제외) */
  total: number;
  byType: EmploymentTypeCounts;
}
export interface WorkforceUpcomingConversionRow {
  name: string;
  orgUnitName: string;
  fromCode: string;
  fromLabel: string;
  toCode: string;
  toLabel: string;
  effectiveDate: string;
  dDay: number;
}
export interface WorkforceContractEndRow {
  name: string;
  orgUnitName: string;
  code: string;
  label: string;
  endDate: string;
  dDay: number;
  /**
   * V70.45: 계약 차수 (BE `employmentHistories.contractSequence` SSoT).
   *   BE 가 확정 가능할 때만 내려준다. 없으면 **추정하지 않고** 고용형태만 표시한다.
   */
  contractSequence?: number;
  /**
   * V70.49: 고용형태별 근속연차 (N년차). BE 가 `employmentHistories` 체인에서 산출한다.
   *   고용형태가 바뀌면 전환일부터 1년차로 초기화된다 (이전 형태 기간 미합산).
   *   확정할 수 없으면 BE 가 필드를 내리지 않는다 — FE 는 추정하지 않는다.
   */
  serviceYear?: number;
}
export interface WorkforceConversionHistoryRow {
  effectiveDate: string;
  name: string;
  orgUnitName: string;
  fromCode: string;
  fromLabel: string;
  toCode: string;
  toLabel: string;
}

export interface WorkforceOverviewReport {
  success: boolean;
  /** 시점성 데이터 기준일 (= asOfDate) */
  asOf: string;
  /** 서버가 확정한 기준월 'YYYY-MM' */
  referenceMonth: string;
  /** 기준월의 연도 */
  referenceYear: number;
  /** 시점성 데이터 기준일: 과거 월=월말 / 당월=오늘 / 미래 월=월말 */
  asOfDate: string;
  /** 기준월이 과거/당월/미래 중 무엇인지 */
  basis: 'past' | 'current' | 'future';
  /** = referenceYear (연간 Section 대상 연도) */
  year: number;
  scope: {
    /**
     * Admin 이 명시 선택한 조직.
     * V70.30: Dashboard 모집단과 조직별 Table row 를 모두 결정한다 (하위 조직 미포함).
     */
    selectedOrgUnitIds: string[];
    selectedOrgUnitCount: number;
    /** = selectedOrgUnitCount */
    orgUnitCount: number;
    employeeCount: number;
  };
  dataQuality: { warnings: string[]; notes: string[] };
  currentSummary: { total: number; byType: WorkforceTypeRow[] };
  monthlyTrend: WorkforceMonthlyTrendRow[];
  monthlyJoinLeave: WorkforceJoinLeaveRow[];
  organizationBreakdown: WorkforceOrgRow[];
  /** V70.36: 계약 만료 현황 (사용자 지정 기간) + 조직별 Rolling 12개월 */
  contractEnd: {
    queryRange: { startDate: string; endDate: string; isDefault: boolean };
    summary: WorkforceContractEndDetail;
    byMonth: WorkforceMonthlyContractEndRow[];
    org: { startDate: string; endDate: string; rows: WorkforceOrgContractEndRow[] };
  };
  upcomingConversions: WorkforceUpcomingConversionRow[];
  upcomingContractEnds: WorkforceContractEndRow[];
  yearlyConversions: {
    year: number;
    total: number;
    pairs: Array<{ fromCode: string; fromLabel: string; toCode: string; toLabel: string; count: number }>;
    items: WorkforceConversionHistoryRow[];
  };
}

export interface UseWorkforceOverviewReportResult {
  report: WorkforceOverviewReport | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * @param referenceMonth 'YYYY-MM' — 생략 시 서버가 KST 당월로 결정한다.
 *
 * race condition 방지:
 *   기준월을 빠르게 여러 번 넘기면 응답 순서가 뒤바뀔 수 있다.
 *   요청마다 증가하는 seq 를 두고 **마지막 요청의 응답만** 반영한다.
 *   (effect cleanup 의 aborted 플래그와 이중으로 방어)
 */
export function useWorkforceOverviewReport(
  referenceMonth?: string,
  contractEndRange?: { startDate: string; endDate: string } | null,
): UseWorkforceOverviewReportResult {
  const [report, setReport] = useState<WorkforceOverviewReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const seqRef = useRef(0);
  const latestAppliedRef = useRef(0);

  useEffect(() => {
    if (!functions) {
      setIsLoading(false);
      setError(new Error('Firebase 가 초기화되지 않았습니다.'));
      return;
    }
    let aborted = false;
    const seq = ++seqRef.current;

    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<
          { referenceMonth?: string; contractEndStart?: string; contractEndEnd?: string },
          WorkforceOverviewReport
        >(functions!, 'getWorkforceOverviewReport');
        const r = await fn({
          ...(referenceMonth ? { referenceMonth } : {}),
          ...(contractEndRange
            ? { contractEndStart: contractEndRange.startDate, contractEndEnd: contractEndRange.endDate }
            : {}),
        });
        // 더 최신 요청이 이미 반영됐으면 이 응답은 버린다
        if (aborted || seq < latestAppliedRef.current) return;
        latestAppliedRef.current = seq;
        setReport(r.data);
      } catch (err: any) {
        if (aborted || seq < latestAppliedRef.current) return;
        latestAppliedRef.current = seq;
        setReport(null);
        setError(err instanceof Error ? err : new Error(err?.message || '인력 현황을 불러오지 못했습니다.'));
      } finally {
        if (!aborted && seq === seqRef.current) setIsLoading(false);
      }
    })();

    return () => { aborted = true; };
  }, [referenceMonth, contractEndRange?.startDate, contractEndRange?.endDate, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return { report, isLoading, error, refetch };
}
